import { useCallback, useEffect, useRef } from 'react';

interface ClipboardPasteOptions {
  onImagesPasted: (files: File[]) => void;
  onError?: (error: string) => void;
  maxImages?: number;
  disabled?: boolean;
  enablePaste?: boolean;
}

interface ClipboardPasteReturn {
  pasteHandler: (event: ClipboardEvent) => void;
  isPasteReady: boolean;
  attachPasteListener: (element: HTMLElement | null) => void;
  detachPasteListener: (element: HTMLElement | null) => void;
}

export const useClipboardImagePaste = (options: ClipboardPasteOptions): ClipboardPasteReturn => {
  const {
    onImagesPasted,
    onError,
    maxImages = 5,
    disabled = false,
    enablePaste = true
  } = options;

  const pasteListenerRef = useRef<((event: ClipboardEvent) => void) | null>(null);

  const pasteHandler = useCallback((event: ClipboardEvent) => {
    if (disabled || !enablePaste) return;

    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    const items = Array.from(clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));

    if (imageItems.length === 0) return;

    // Prevent default paste behavior for images
    event.preventDefault();
    event.stopPropagation();

    if (imageItems.length > maxImages) {
      onError?.(`Maximum ${maxImages} images allowed. Found ${imageItems.length} images.`);
      return;
    }

    const files: File[] = [];
    let processedCount = 0;

    imageItems.forEach((item, index) => {
      const blob = item.getAsFile();
      if (!blob) {
        processedCount++;
        if (processedCount === imageItems.length && files.length > 0) {
          onImagesPasted(files);
        }
        return;
      }

      // Create a proper File object with a meaningful name
      const fileExtension = blob.type.split('/')[1] || 'png';
      const fileName = `pasted-image-${Date.now()}-${index + 1}.${fileExtension}`;
      
      const file = new File([blob], fileName, {
        type: blob.type,
        lastModified: Date.now()
      });

      files.push(file);
      processedCount++;

      if (processedCount === imageItems.length && files.length > 0) {
        onImagesPasted(files);
      }
    });

    // Handle case where no valid files were found
    if (imageItems.length > 0 && files.length === 0) {
      onError?.('No valid image files found in clipboard');
    }
  }, [disabled, enablePaste, maxImages, onImagesPasted, onError]);

  const attachPasteListener = useCallback((element: HTMLElement | null) => {
    if (!element || !enablePaste) return;

    // Store the handler reference
    pasteListenerRef.current = pasteHandler;
    
    // Add the event listener
    element.addEventListener('paste', pasteHandler);
    
    // Make element focusable if it isn't already
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '-1');
    }
  }, [pasteHandler, enablePaste]);

  const detachPasteListener = useCallback((element: HTMLElement | null) => {
    if (!element || !pasteListenerRef.current) return;

    element.removeEventListener('paste', pasteListenerRef.current);
    pasteListenerRef.current = null;
  }, []);

  // Check if paste is ready (clipboard API is available)
  const isPasteReady = useCallback(() => {
    return !disabled && enablePaste && typeof navigator !== 'undefined' && 
           typeof ClipboardEvent !== 'undefined';
  }, [disabled, enablePaste]);

  return {
    pasteHandler,
    isPasteReady: isPasteReady(),
    attachPasteListener,
    detachPasteListener
  };
};

export default useClipboardImagePaste;